import { cn } from '../lib/cn';

/**
 * A single shimmering placeholder block (moving-highlight sweep — see `.skeleton`
 * in styles.css). Size/shape it with className, e.g.
 *   <Skeleton className="h-4 w-40 rounded" />
 * Compose several to mock a row or card while data loads, so lists don't flash an
 * empty "(0)" state before the query resolves. Decorative → hidden from a11y tree.
 */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton', className)} aria-hidden="true" />;
}

/**
 * Generic full-page loading skeleton — a header + a stack of content blocks. Use as
 * the loading state of a data-fetching page so it shimmers instead of flashing a
 * "Loading…" string. Pass `rows` to tune the body length.
 */
export function PageSkeleton({ rows = 6, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto', className)} aria-busy="true">
      <div className="space-y-2 mb-8">
        <Skeleton className="h-3 w-16 rounded" />
        <Skeleton className="h-8 w-64 max-w-full rounded" />
        <Skeleton className="h-4 w-80 max-w-full rounded" />
      </div>
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} className="h-14 rounded-lg" />
        ))}
      </div>
    </div>
  );
}
